import Loader from '@/components/ui/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmpty } from 'lodash';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useAppSelector } from '../../../../app/redux/hooks';
import { buildUserWithValuesFromFlatList, getProfileData } from '../../../../app/user/profile/profileUtils';
import DependencyField from '../../DependencyField';
import { generateDefaultValues } from '../../formUtils';
import PaginatedFooter from '../PaginatedFooter';
import useQuestionary from '../hooks/useQuestionary';
import { FormProps } from '../type';
import { generateTrackingInfo } from '../utils/generateTrackingInfo';

const ProfileCompletionForm = ({
  questionData,
  currentItemIndex,
  nextItem,
  previousItem,
  initialQuestionsCount,
  onSubmit
}: FormProps) => {
  const profileDetails = useAppSelector((state) => state.profile?.profile);
  const { isLoading, user } = useQuestionary();
  const { group, field } = questionData;
  // Compile list of all fields and dependent fields included in this form.
  const dependents = questionData?.fieldDependents;
  const currentFields = [field];
  dependents.forEach((dependent) => {
    currentFields.push(dependent.fieldDependentName);
  });

  const handleSubmit = () => {
    const values = buildUserWithValuesFromFlatList(fieldChange, profileDetails);
    onSubmit(values);
  };

  // The field data can be any type so we need to handle some special cases
  const emptyFieldCheck = (data) => {
    // If the type is boolean isempty will return true when the value is true
    if (typeof data == 'boolean') {
      if (data === true || data === false) {
        return false;
      }
      return true;
    } else {
      return isEmpty(data);
    }
  };

  const fieldData = getProfileData(user, profileDetails);
  // Form components are keyed flatly so ensure default values are as well
  const defaultValues = generateDefaultValues(fieldData, currentFields);
  const methods = useForm({ defaultValues });
  const fieldChange = methods.watch();
  const trackingInfo = generateTrackingInfo(questionData);
  // Handle allow form submit status
  let selected = true;
  // No value or root field value
  if (emptyFieldCheck(fieldChange) || emptyFieldCheck(fieldChange[field])) {
    selected = false;
  }
  // / otherField is selected and it is empty
  else if (
    fieldChange[field] === questionData.otherRefTerm &&
    emptyFieldCheck(fieldChange[`${field}_other`]) &&
    emptyFieldCheck(fieldData[field]?.otherValue)
  ) {
    selected = false;
  } else {
    currentFields.forEach((value) => {
      // Root field is empty.
      if (value == field && emptyFieldCheck(fieldChange[field])) {
        selected = false;
      }
      // Check if we're a dependent field (this only works for one level of hierarchy)
      dependents.forEach((dependent) => {
        // this field is rendered if the selected root value matches the dependency value
        if (value == dependent.fieldDependentName) {
          if (Array.isArray(fieldChange[field]) && dependent.values.some((r) => fieldChange[field].includes(r))) {
            // Is array and dependency value selected.
            selected = false;
            if (!emptyFieldCheck(fieldChange[value])) {
              selected = true;
            }
          } else if (dependent.values.includes(fieldChange[field])) {
            selected = false;
            if (!emptyFieldCheck(fieldChange[value])) {
              selected = true;
            }
          }
        }
      });
    });
  }
  return (
    <>
      <div className="overflow-x-hidden overflow-y-auto pretty-scroll">
        <Loader loaded={!isLoading}>
          <div className="flex flex-col pl-1">
            <h2 className="md:display-title-3xl display-title-xl md:mb-6 mb-2 text-center">{questionData?.heading}</h2>
            <div className="flex flex-col justify-start gap-2">
              <DependencyField
                methods={methods}
                key={field}
                field={profileDetails.fieldGroups[group].fields[field]}
                userData={fieldData}
                requiredDependentsOnDisplay={true}
                displayLabel={false}
              />
            </div>
            {questionData.groupDescription && (
              <div className="text-left flex items-start mt-3 px-4 py-2 border-2 border-gray-200 rounded-lg">
                <FontAwesomeIcon className="mt-[.4rem] mr-2 text-gray-700" icon={['far', 'info-circle']} />
                <span
                  className="content-formatted max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: questionData.groupDescription || '' }}
                />
              </div>
            )}
          </div>
        </Loader>
      </div>
      <div className="dialog-content-footer mt-4 md:mb-2">
        <PaginatedFooter
          currentItemIndex={currentItemIndex}
          initialQuestionsCount={initialQuestionsCount}
          nextItem={nextItem}
          previousItem={previousItem}
          currentIsAnswered={selected}
          trackingInfo={trackingInfo!}
          onSubmit={handleSubmit}
        />
      </div>
    </>
  );
};

export default ProfileCompletionForm;
